/**
 * Keyboard Shortcuts Hook
 *
 * Manages global keyboard shortcuts with customization support.
 */
import { useEffect, useCallback, useRef } from 'react';

export interface KeyBinding {
  key: string;
  modifiers?: {
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
  };
}

export interface Shortcut {
  id: string;
  label: string;
  description?: string;
  binding: KeyBinding;
  action: () => void | Promise<void>;
  when?: () => boolean;
  preventDefault?: boolean;
}

interface ShortcutManagerOptions {
  enabled?: boolean;
  preventDefault?: boolean;
}

/**
 * Format a key binding for display.
 */
export function formatKeyBinding(binding: KeyBinding): string {
  const parts: string[] = [];
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  if (binding.modifiers?.ctrl) {
    parts.push(isMac ? '⌃' : 'Ctrl');
  }
  if (binding.modifiers?.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }
  if (binding.modifiers?.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (binding.modifiers?.meta) {
    parts.push(isMac ? '⌘' : 'Win');
  }

  // Format key
  let key = binding.key.toUpperCase();
  if (key === ' ') key = 'Space';
  if (key === 'ARROWUP') key = '↑';
  if (key === 'ARROWDOWN') key = '↓';
  if (key === 'ARROWLEFT') key = '←';
  if (key === 'ARROWRIGHT') key = '→';
  if (key === 'ENTER') key = '↵';
  if (key === 'ESCAPE') key = 'Esc';
  if (key === 'BACKSPACE') key = '⌫';
  if (key === 'TAB') key = '⇥';

  parts.push(key);

  return isMac ? parts.join('') : parts.join('+');
}

/**
 * Parse a keyboard event into a KeyBinding.
 */
export function parseKeyEvent(e: KeyboardEvent): KeyBinding {
  return {
    key: e.key.toLowerCase(),
    modifiers: {
      ctrl: e.ctrlKey,
      alt: e.altKey,
      shift: e.shiftKey,
      meta: e.metaKey,
    },
  };
}

/**
 * Check if two key bindings match.
 */
export function matchBinding(a: KeyBinding, b: KeyBinding): boolean {
  if (a.key.toLowerCase() !== b.key.toLowerCase()) return false;

  const am = a.modifiers || {};
  const bm = b.modifiers || {};

  return (
    !!am.ctrl === !!bm.ctrl &&
    !!am.alt === !!bm.alt &&
    !!am.shift === !!bm.shift &&
    !!am.meta === !!bm.meta
  );
}

/**
 * Create default shortcuts.
 */
export function createDefaultShortcuts(handlers: {
  toggleCommandPalette?: () => void;
  saveFile?: () => void;
  toggleChat?: () => void;
  togglePreview?: () => void;
  toggleFiles?: () => void;
  newFile?: () => void;
  closeTab?: () => void;
  find?: () => void;
  findReplace?: () => void;
  formatCode?: () => void;
  undo?: () => void;
  redo?: () => void;
  goToLine?: () => void;
  goToFile?: () => void;
}): Shortcut[] {
  const shortcuts: Shortcut[] = [];

  if (handlers.toggleCommandPalette) {
    shortcuts.push({
      id: 'command-palette',
      label: 'Command Palette',
      description: 'Open command palette',
      binding: { key: 'p', modifiers: { ctrl: true, shift: true } },
      action: handlers.toggleCommandPalette,
    });
    // Also Cmd+K on Mac
    shortcuts.push({
      id: 'command-palette-alt',
      label: 'Command Palette',
      binding: { key: 'k', modifiers: { meta: true } },
      action: handlers.toggleCommandPalette,
    });
  }

  if (handlers.saveFile) {
    shortcuts.push({
      id: 'save-file',
      label: 'Save File',
      description: 'Save current file',
      binding: { key: 's', modifiers: { ctrl: true } },
      action: handlers.saveFile,
    });
    shortcuts.push({
      id: 'save-file-mac',
      label: 'Save File',
      binding: { key: 's', modifiers: { meta: true } },
      action: handlers.saveFile,
    });
  }

  if (handlers.toggleChat) {
    shortcuts.push({
      id: 'toggle-chat',
      label: 'Toggle AI Chat',
      description: 'Show/hide AI chat panel',
      binding: { key: 'j', modifiers: { ctrl: true } },
      action: handlers.toggleChat,
    });
  }

  if (handlers.togglePreview) {
    shortcuts.push({
      id: 'toggle-preview',
      label: 'Toggle Preview',
      description: 'Show/hide preview panel',
      binding: { key: 'p', modifiers: { ctrl: true, alt: true } },
      action: handlers.togglePreview,
    });
  }

  if (handlers.toggleFiles) {
    shortcuts.push({
      id: 'toggle-files',
      label: 'Toggle Files',
      description: 'Show/hide files panel',
      binding: { key: 'b', modifiers: { ctrl: true } },
      action: handlers.toggleFiles,
    });
    shortcuts.push({
      id: 'toggle-files-mac',
      label: 'Toggle Files',
      binding: { key: 'b', modifiers: { meta: true } },
      action: handlers.toggleFiles,
    });
  }

  if (handlers.newFile) {
    shortcuts.push({
      id: 'new-file',
      label: 'New File',
      description: 'Create a new file',
      binding: { key: 'n', modifiers: { ctrl: true } },
      action: handlers.newFile,
    });
    shortcuts.push({
      id: 'new-file-mac',
      label: 'New File',
      binding: { key: 'n', modifiers: { meta: true } },
      action: handlers.newFile,
    });
  }

  if (handlers.closeTab) {
    shortcuts.push({
      id: 'close-tab',
      label: 'Close Tab',
      description: 'Close current tab',
      binding: { key: 'w', modifiers: { ctrl: true } },
      action: handlers.closeTab,
    });
    shortcuts.push({
      id: 'close-tab-mac',
      label: 'Close Tab',
      binding: { key: 'w', modifiers: { meta: true } },
      action: handlers.closeTab,
    });
  }

  if (handlers.find) {
    shortcuts.push({
      id: 'find',
      label: 'Find',
      description: 'Find in file',
      binding: { key: 'f', modifiers: { ctrl: true } },
      action: handlers.find,
    });
    shortcuts.push({
      id: 'find-mac',
      label: 'Find',
      binding: { key: 'f', modifiers: { meta: true } },
      action: handlers.find,
    });
  }

  if (handlers.findReplace) {
    shortcuts.push({
      id: 'find-replace',
      label: 'Find and Replace',
      description: 'Find and replace in file',
      binding: { key: 'h', modifiers: { ctrl: true } },
      action: handlers.findReplace,
    });
    shortcuts.push({
      id: 'find-replace-mac',
      label: 'Find and Replace',
      binding: { key: 'h', modifiers: { meta: true, alt: true } },
      action: handlers.findReplace,
    });
  }

  if (handlers.formatCode) {
    shortcuts.push({
      id: 'format-code',
      label: 'Format Document',
      description: 'Format current document',
      binding: { key: 'f', modifiers: { ctrl: true, shift: true } },
      action: handlers.formatCode,
    });
    shortcuts.push({
      id: 'format-code-mac',
      label: 'Format Document',
      binding: { key: 'f', modifiers: { meta: true, shift: true } },
      action: handlers.formatCode,
    });
  }

  if (handlers.goToLine) {
    shortcuts.push({
      id: 'go-to-line',
      label: 'Go to Line',
      description: 'Jump to a specific line',
      binding: { key: 'g', modifiers: { ctrl: true } },
      action: handlers.goToLine,
    });
    shortcuts.push({
      id: 'go-to-line-mac',
      label: 'Go to Line',
      binding: { key: 'g', modifiers: { meta: true } },
      action: handlers.goToLine,
    });
  }

  if (handlers.goToFile) {
    shortcuts.push({
      id: 'go-to-file',
      label: 'Quick Open',
      description: 'Quick open file',
      binding: { key: 'p', modifiers: { ctrl: true } },
      action: handlers.goToFile,
    });
    shortcuts.push({
      id: 'go-to-file-mac',
      label: 'Quick Open',
      binding: { key: 'p', modifiers: { meta: true } },
      action: handlers.goToFile,
    });
  }

  return shortcuts;
}

/**
 * Hook for managing keyboard shortcuts.
 */
export function useKeyboardShortcuts(
  shortcuts: Shortcut[],
  options: ShortcutManagerOptions = {}
): {
  getShortcuts: () => Shortcut[];
  formatBinding: (binding: KeyBinding) => string;
} {
  const { enabled = true, preventDefault = true } = options;
  const shortcutsRef = useRef(shortcuts);

  // Keep shortcuts ref updated
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Ignore if typing in an input (unless it's an escape or command palette shortcut)
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      const eventBinding = parseKeyEvent(e);

      // Phase A: shortcuts allowed to fire even when target is an
      // input-like element (INPUT/TEXTAREA/contenteditable). Save MUST
      // be in this list — Monaco renders the cursor target as a
      // `div[contenteditable=true]`, and without the allowlist Cmd/Ctrl+S
      // typed inside the editor was a silent no-op. Other shortcuts
      // (find, format, goToFile) stay gated so the user can type those
      // literal characters into the editor.
      const INPUT_ALLOWED_IDS = new Set([
        'command-palette',
        'command-palette-alt',
        'save-file',
        'save-file-mac',
      ]);

      for (const shortcut of shortcutsRef.current) {
        if (matchBinding(shortcut.binding, eventBinding)) {
          // Skip if in input and not allowed
          if (isInput && !INPUT_ALLOWED_IDS.has(shortcut.id)) {
            continue;
          }

          // Check condition
          if (shortcut.when && !shortcut.when()) {
            continue;
          }

          // Prevent default
          if (shortcut.preventDefault ?? preventDefault) {
            e.preventDefault();
            e.stopPropagation();
          }

          // Execute action
          shortcut.action();
          return;
        }
      }
    },
    [enabled, preventDefault]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  return {
    getShortcuts: () => shortcutsRef.current,
    formatBinding: formatKeyBinding,
  };
}

export default useKeyboardShortcuts;
